import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { ShareAccessSearchItem } from './ShareAccessSearchItem';
import { ShareAccessTable } from './ShareAccessTable';
import { ShareAccessWithFragmentProps } from './ShareAccessWithFragment.props';
import { useShareAccessForm } from './useShareAccessForm';
import { LiveSearchResponse } from '../../../../../@types';
import {} from '../../../../../components/atoms/Box';
import { LiveSearchBox } from '../../../../../components/atoms/LiveSearchBox';
import { useSearchAllNodesByName } from '../../../../../queries/live-search-query';
import { ReportEditConfirmDialog } from '../ReportEditFragment/ReportEditConfirmDialog';
import {
  FIELD_HINT_SX,
  FORM_STACK_SX,
  SECTION_HEADING_SX,
  TAB_BODY_SX,
} from '../ReportEditFragment/reportEditDialog.styles';
import { ReportEditDialogFooter } from '../ReportEditFragment/ReportEditDialogFooter';

export const ShareAccessWithFragment: React.FC<ShareAccessWithFragmentProps> = ({
  rowData,
  savedData,
  handleRefetch,
}) => {
  const form = useShareAccessForm(savedData, handleRefetch, rowData);
  const searchNodeQuery = useSearchAllNodesByName();

  return (
    <>
      <Box sx={TAB_BODY_SX}>
        <Stack spacing={2.5} sx={FORM_STACK_SX}>
          <Box component="section">
            <Typography sx={SECTION_HEADING_SX}>Add people or locations</Typography>
            <Box sx={{ mt: 1 }}>
              <LiveSearchBox
                title="Search people or locations"
                timeOffset={400}
                value={form.searchData || ''}
                onClearValue={form.onClearSearch}
                renderItem={(
                  { key, ...liProps }: { key: string } & React.HTMLAttributes<HTMLLIElement>,
                  option: LiveSearchResponse
                ) => <ShareAccessSearchItem key={key} liProps={liProps} data={option} />}
                onChange={async (liveSearchResponse: LiveSearchResponse) =>
                  form.onPickSearchResult(liveSearchResponse.id, liveSearchResponse.name)
                }
                onApiInvoke={async (searchText) => searchNodeQuery.mutateAsync(searchText)}
              />
              <Typography variant="caption" sx={FIELD_HINT_SX}>
                Pick a user or location, set their permissions below, then save.
              </Typography>
            </Box>
          </Box>

          <Box component="section">
            <Typography sx={SECTION_HEADING_SX}>Shared with</Typography>
            <Box sx={{ mt: 1 }}>
              <ShareAccessTable
                rows={form.tableRowData}
                onTogglePermission={form.onTogglePermission}
                onRemove={form.requestRemoval}
              />
            </Box>
          </Box>
        </Stack>
      </Box>

      <ReportEditDialogFooter
        primaryLabel="Save sharing"
        onPrimary={form.handleSave}
        primaryDisabled={form.isSaving}
      />

      <ReportEditConfirmDialog
        open={Boolean(form.pendingRemoval)}
        title="Remove access"
        description={`${form.pendingRemoval?.name || 'This recipient'} will no longer be able to see this report.`}
        confirmLabel="Remove"
        destructive
        onCancel={form.cancelRemoval}
        onConfirm={form.handleConfirmRemoval}
      />
    </>
  );
};
